import { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Cpu, Loader2 } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { AriaOrb } from "@/components/AriaOrb";

const HEADER_ANIM = { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.6 } };
const FORM_ANIM = { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.6, delay: 0.15 } };

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(name, email, password);
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="aria-void aria-grid min-h-screen w-full flex items-center justify-center p-6 relative">
      <div className="relative z-10 w-full max-w-md">
        <motion.div
          {...HEADER_ANIM}
          className="flex flex-col items-center mb-8"
        >
          <AriaOrb state="idle" size={140} />
          <h1 className="font-heading text-4xl sm:text-5xl font-light tracking-tighter text-white mt-4 text-glow">
            KAI
          </h1>
          <p className="font-mono text-xs tracking-[0.3em] uppercase text-cyan-400/80 mt-2">
            Sistema de inteligencia artificial
          </p>
        </motion.div>

        <motion.form
          {...FORM_ANIM}
          onSubmit={submit}
          className="backdrop-blur-2xl bg-black/50 border border-white/10 rounded-2xl p-8 shadow-[0_8px_60px_rgba(0,240,255,0.08)]"
          data-testid="auth-form"
        >
          <div className="flex gap-2 mb-6 p-1 bg-white/5 rounded-full border border-white/10">
            {["login", "register"].map((m) => (
              <button
                key={m}
                type="button"
                data-testid={`tab-${m}`}
                onClick={() => { setMode(m); setError(""); }}
                className={`flex-1 py-2 rounded-full text-sm font-medium transition-all ${
                  mode === m
                    ? "bg-cyan-500/20 text-cyan-200 shadow-[0_0_15px_rgba(34,211,238,0.3)]"
                    : "text-slate-400 hover:text-slate-200"
                }`}
              >
                {m === "login" ? "Acceder" : "Crear cuenta"}
              </button>
            ))}
          </div>

          {mode === "register" && (
            <input
              data-testid="name-input"
              type="text"
              placeholder="Nombre"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="w-full mb-4 bg-black/40 border border-white/10 rounded-xl px-5 py-3 text-slate-200 placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-cyan-500/50 focus:border-cyan-500/50 transition-all"
            />
          )}
          <input
            data-testid="email-input"
            type="email"
            placeholder="Correo electrónico"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="w-full mb-4 bg-black/40 border border-white/10 rounded-xl px-5 py-3 text-slate-200 placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-cyan-500/50 focus:border-cyan-500/50 transition-all"
          />
          <input
            data-testid="password-input"
            type="password"
            placeholder="Contraseña"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="w-full mb-6 bg-black/40 border border-white/10 rounded-xl px-5 py-3 text-slate-200 placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-cyan-500/50 focus:border-cyan-500/50 transition-all"
          />

          {error && (
            <p data-testid="auth-error" className="text-sm text-red-400 mb-4 font-mono">
              {error}
            </p>
          )}

          <button
            type="submit"
            data-testid="submit-button"
            disabled={busy}
            className="w-full flex items-center justify-center gap-2 bg-cyan-500/90 hover:bg-cyan-400 text-slate-950 font-semibold py-3 rounded-xl transition-all shadow-[0_0_20px_rgba(34,211,238,0.4)] disabled:opacity-60 active:scale-[0.98]"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Cpu className="w-4 h-4" />}
            {mode === "login" ? "Iniciar sistema" : "Registrarse"}
          </button>
        </motion.form>
      </div>
    </div>
  );
}
